import React, { useState, useEffect } from 'react';
import { Bell, X, CheckCircle, AlertCircle, Clock, Euro, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const NotificationBell = () => {
  const { user, isAdmin } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (isAdmin) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000); // Refresh every 30s
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/admin/notifications');
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/admin/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/admin/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'vendor_request':
        return <Users className="h-5 w-5 text-[#FFB800]" />;
      case 'payout_request':
        return <Euro className="h-5 w-5 text-[#00FF88]" />;
      case 'subscription_expiring':
        return <Clock className="h-5 w-5 text-[#FF3B5C]" />;
      case 'new_subscription':
        return <CheckCircle className="h-5 w-5 text-[#00FFFF]" />;
      case 'new_transaction':
        return <Euro className="h-5 w-5 text-[#00FF88]" />;
      case 'new_ticket':
        return <AlertCircle className="h-5 w-5 text-[#FFB800]" />;
      case 'account_purged':
        return <AlertCircle className="h-5 w-5 text-[#FF3B5C]" />;
      default:
        return <AlertCircle className="h-5 w-5 text-[#B8B8BA]" />;
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);
    
    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Il y a ${Math.floor(diffInMinutes / 60)}h`;
    return `Il y a ${Math.floor(diffInMinutes / 1440)}j`;
  };

  if (!isAdmin) return null;

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 rounded-lg hover:bg-[#222226] transition-colors"
        data-testid="notification-bell-button"
      >
        <Bell className="h-5 w-5 text-[#B8B8BA] hover:text-[#00FFFF] transition-colors" />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-0.5 -right-0.5 h-4.5 w-4.5 min-w-[18px] bg-[#FF3B5C] rounded-full flex items-center justify-center text-white text-[10px] font-bold ring-2 ring-[#121214]"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {showDropdown && (
          <>
            <div 
              className="fixed inset-0 z-40 bg-black/20 md:bg-transparent" 
              onClick={() => setShowDropdown(false)}
            />
            {/* Desktop dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
              className="hidden md:block absolute right-0 mt-2 w-[380px] bg-[#1A1A1D] rounded-xl shadow-2xl border border-[#3A3A3D] z-50"
              data-testid="notification-dropdown"
            >
              <div className="p-4 border-b border-[#3A3A3D] flex items-center justify-between">
                <h3 className="text-white font-bold">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-[#00FFFF] text-xs hover:underline"
                    data-testid="mark-all-read-button"
                  >
                    Tout marquer comme lu
                  </button>
                )}
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center" data-testid="no-notifications">
                    <Bell className="h-10 w-10 text-[#3A3A3D] mx-auto mb-2" />
                    <p className="text-[#6B6B6E] text-sm">Aucune notification</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 border-b border-[#3A3A3D]/50 hover:bg-[#222226] transition-colors cursor-pointer ${
                        !notification.is_read ? 'bg-[#00FFFF]/5' : ''
                      }`}
                      onClick={() => !notification.is_read && markAsRead(notification.id)}
                      data-testid={`notification-item-${notification.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-snug ${
                            !notification.is_read ? 'text-white font-semibold' : 'text-[#E8E8E8]'
                          }`}>
                            {notification.title}
                          </p>
                          <p className="text-[#B8B8BA] text-xs mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-[#6B6B6E] text-[11px] mt-1">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <div className="flex-shrink-0 mt-1.5">
                            <div className="h-2 w-2 bg-[#00FFFF] rounded-full" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              {notifications.length > 0 && (
                <div className="p-2.5 border-t border-[#3A3A3D] text-center">
                  <button 
                    className="text-[#6B6B6E] text-xs hover:text-[#00FFFF] transition-colors"
                    onClick={() => setShowDropdown(false)}
                  >
                    Fermer
                  </button>
                </div>
              )}
            </motion.div>

            {/* Mobile full-width panel */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="md:hidden fixed top-14 left-0 right-0 mx-3 bg-[#1A1A1D] rounded-xl shadow-2xl border border-[#3A3A3D] z-50 max-h-[calc(100vh-80px)] flex flex-col"
              data-testid="notification-dropdown-mobile"
            >
              <div className="p-3.5 border-b border-[#3A3A3D] flex items-center justify-between flex-shrink-0">
                <h3 className="text-white font-bold text-sm">Notifications</h3>
                <div className="flex items-center gap-3">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-[#00FFFF] text-xs hover:underline"
                    >
                      Tout lire
                    </button>
                  )}
                  <button 
                    onClick={() => setShowDropdown(false)}
                    className="text-[#6B6B6E] hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto flex-1">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center">
                    <Bell className="h-8 w-8 text-[#3A3A3D] mx-auto mb-2" />
                    <p className="text-[#6B6B6E] text-sm">Aucune notification</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-3.5 py-3 border-b border-[#3A3A3D]/50 active:bg-[#222226] transition-colors ${
                        !notification.is_read ? 'bg-[#00FFFF]/5' : ''
                      }`}
                      onClick={() => !notification.is_read && markAsRead(notification.id)}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-snug ${
                            !notification.is_read ? 'text-white font-semibold' : 'text-[#E8E8E8]'
                          }`}>
                            {notification.title}
                          </p>
                          <p className="text-[#B8B8BA] text-xs mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-[#6B6B6E] text-[11px] mt-1">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <div className="flex-shrink-0 mt-1.5">
                            <div className="h-2 w-2 bg-[#00FFFF] rounded-full" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;